import { devLog } from '../core/utils';
import { errors, SUBMISSION_UPLOAD_TYPE_PATTERN } from '../core/config';
import { NUMBER_UPLOAD_SIZE_LIMIT_RAW, NUMBER_UPLOAD_DURATION_LIMIT_RAW } from './../../design-tokens/build/dictionary';
/**
 * Handle file input (image only rt now)
 *
 * bind to the `change` event to react to the file's url
 */
export default {
  name: 'upload',
  data() {
    return {
      uploadType: null, // the image type of the current file
      uploadName: null, // the name of the current file
      uploadUrl: null, // the object url of the current file
      enabled: false, // if this functionality is enabled
      image: false, // if the upoad is an image
      video: false, // if the upload is a video
      file: null, // the file
      videoDuration: 0, // duration if the upload is a video
      busy: false, // we're busy if we're processing an upload
      aspectRatio: '', // file aspect ratio
      videoType: '', // type of video so that we can branch for quicktime
    };
  },
  created() {
    this.log('created');
    this.checkSupport();
  },
  methods: {
    /**
     * Check if this mixin is supported
     */
    checkSupport() {
      if (!window.URL) {
        throw new Error(errors.UPLOAD_UNSUPPORTED);
      }
      this.enabled = true;
    },

    /**
     * Check for valid file type and set flags
     *
     * @param {string} [type='']
     * @returns {null}
     */
    setType(type = '') {
      const typeMatch = type.match(SUBMISSION_UPLOAD_TYPE_PATTERN);

      if (!typeMatch || !typeMatch.length) {
        throw new Error(errors.UPLOAD_TYPE_INVALID);
      }

      if (typeMatch[1]) {
        // image
        this.image = true;
        return;
      }

      if (typeMatch[2]) {
        // video
        this.video = true;
        this.videoType = typeMatch[3]; // eslint-disable-line
        return;
      }

      throw new Error(errors.UPLOAD_TYPE_INVALID);
    },

    /**
     * Check the file size
     *
     * @param {Number} bytes
     */
    checkSize(bytes = 0) {
      const size = bytes / 1024 / 1024;
      if (!bytes || size > NUMBER_UPLOAD_SIZE_LIMIT_RAW) {
        throw new Error(errors.UPLOAD_SIZE_INVALID);
      }
    },

    /**
     * Make sure that the dom is ready for these bindings
     *
     */
    checkDom() {
      if (!this.$refs.inputFile) {
        throw new Error('no input');
      }
    },

    /**
     * Handle the change
     *
     */
    async handleChange() {
      if (this.busy) {
        return;
      }
      this.busy = true;
      this.checkDom();

      if (!this.enabled) {
        return;
      }

      const { files } = this.$refs.inputFile;

      if (!files || !files.length) {
        // nothing selected
        return;
      }

      const file = files[0];

      this.log('file', file);

      try {
        this.checkSize(file.size);
      } catch (e) {
        e.message = errors.UPLOAD_SIZE_INVALID;
        this.$emit('error', e);
        this.busy = false;
        return;
      }

      try {
        this.setType(file.type);
      } catch (e) {
        e.message = errors.UPLOAD_TYPE_INVALID;
        this.$emit('error', e);
        this.busy = false;
        return;
      }

      this.file = file;
      this.uploadType = file.type;
      this.uploadName = file.name;
      this.clear();

      this.uploadUrl = URL.createObjectURL(file);

      try {
        if (this.video) {
          await this.setVideoMetadata();
        } else {
          await this.setImageMetaData();
        }
      } catch (e) {
        this.$emit('error', e);
        this.busy = false;
        return;
      }

      this.busy = false;
      this.emitSrc();
    },

    /**
     * Load the resource and read some metadata
     *
     * @returns {Promise.<null>}
     */
    setImageMetaData() {
      const image = new Image();
      return new Promise((resolve, reject) => {
        image.onload = () => {
          this.aspectRatio = `${image.naturalWidth}:${image.naturalHeight}`;
          resolve();
        };

        image.onerror = (e) => {
          e.message = errors.UPLOAD_OPEN;
          reject(e);
        };

        image.src = this.uploadUrl;
      });
    },


    /**
     * Load the resource and read some metadata
     *
     * @returns {Promise.<null, Error>}
     */
    setVideoMetadata() {
      const video = document.createElement('video');
      video.preload = 'metadata';

      return new Promise((resolve, reject) => {
        video.onloadedmetadata = () => {
          this.videoDuration = video.duration;

          if (this.videoDuration > NUMBER_UPLOAD_DURATION_LIMIT_RAW) {
            const e = new Error();
            e.message = errors.UPLOAD_DURATION_INVALID;
            reject(e);
            return;
          }
          this.aspectRatio = `${video.videoWidth}:${video.videoHeight}`;
          resolve();
        };

        video.onerror = (e) => {
          e.message = errors.UPLOAD_OPEN;
          reject(e);
        };

        video.src = this.uploadUrl;
      });
    },

    /**
     * Clear the last uploadUrl
     *
     */
    clear() {
      if (this.uploadUrl) {
        URL.revokeObjectURL(this.uploadUrl);
      }
    },

    /**
     * Reset the value
     *
     */
    resetValue() {
      this.busy = false;
      this.uploadName = null;
      this.uploadType = null;
      this.image = false;
      this.video = false;
      this.file = null;
      this.videoDuration = 0;
      this.aspectRatio = '';
      this.videoType = '';

      // reset ui;
      this.checkDom();
      this.$refs.inputFile.value = null;
    },

    /**
     * emit the image url after its loaded.
     *
     */
    emitSrc() {
      const file = new File([this.file], this.uploadName, { type: this.uploadType });

      this.$emit('change', {
        image: this.image,
        video: this.video,
        videoType: this.videoType,
        mimetype: this.uploadType,
        url: this.uploadUrl,
        name: this.uploadName,
        duration: this.videoDuration,
        aspectRatio: this.aspectRatio,
        file,
      });

      // reset the value after we emit so that the user can upload a new image and `change` event will be fired
      this.resetValue();
    },

    log: devLog.bind(this, 'Upload Mixin:', true),
  }
};
